import { useState, type ReactNode } from "react";
import type { JSX } from "react";
import {
  ASK_USER_MARKER_PREFIX,
  ASK_USER_MARKER_CLOSER,
  type AgentFailure,
  type ResolvedDecision,
} from "@magnis/client-core";
import type {
  ChatMessageAttachment,
  PendingToolCall,
  CompletedToolResult,
  ContentBlock,
} from "../../modules/episodes/types";
import type { AppRuntime } from "../../runtime/contracts/runtime";
import { Icon } from "../ui/Icon";
import { ExpandableEntityCard } from "./ExpandableEntityCard";
import { DecisionSummary } from "./DecisionSummary";
import { MarkdownText } from "./MarkdownText";
import { agentFailureCopy } from "./agentFailureCopy";
import { extractTriggerPrompt } from "./triggerMessage";

const ATTACHMENTS_SEPARATOR = "\n\n---attachments---\n";

function UserMessageBubble({
  content,
  displayContent,
}: {
  readonly content: string;
  readonly displayContent?: string;
  readonly attachments?: readonly ChatMessageAttachment[];
  readonly runtime?: AppRuntime;
}): JSX.Element {
  const visibleText = displayContent ?? visibleUserMessageText(content);
  return (
    <div className="rounded-xl border border-edge bg-surface-secondary px-3 py-2.5">
      <MarkdownText text={visibleText} className="text-[13px] leading-[1.55] text-agent-text" />
    </div>
  );
}

function visibleUserMessageText(content: string): string {
  let text = content;
  const sepIdx = text.indexOf(ATTACHMENTS_SEPARATOR);
  if (sepIdx !== -1) text = text.slice(0, sepIdx);

  // Strip the machine-readable wrapper that AskUserCard adds around the
  // user's selected options: `[User selected from ask_user options]\n…\n
  // [Proceed with the selected option — do NOT ask again]`. The middle
  // payload is what the user actually picked — show that as the bubble
  // body so the feed doesn't look like the user's reply vanished.
  if (text.startsWith(ASK_USER_MARKER_PREFIX)) {
    text = text.slice(ASK_USER_MARKER_PREFIX.length).replace(/^\n/, "");
    if (text.endsWith(ASK_USER_MARKER_CLOSER)) {
      text = text.slice(0, text.length - ASK_USER_MARKER_CLOSER.length).replace(/\n$/, "");
    }
  }
  return text;
}

function sameVisibleUserMessage(left: string, right: string): boolean {
  const leftText = visibleUserMessageText(left);
  const rightText = visibleUserMessageText(right);
  const leftId = contentId(leftText);
  const rightId = contentId(rightText);
  return leftId !== null && rightId !== null ? leftId === rightId : leftText === rightText;
}

function MessageAttachments({
  attachments,
  runtime,
}: {
  readonly attachments: readonly ChatMessageAttachment[];
  readonly runtime: AppRuntime;
}): JSX.Element {
  return (
    <div className="space-y-1">
      {attachments.map((att, i) => (
        <ExpandableEntityCard
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          key={`att-${i}`}
          schemaId={att.schemaId}
          data={att.data}
          runtime={runtime}
        />
      ))}
    </div>
  );
}

function AssistantBubble({
  content,
  isStreaming,
}: {
  readonly content: string;
  readonly isStreaming: boolean;
}): JSX.Element {
  if (isStreaming) {
    return (
      <div className="px-3 text-[13px] leading-[1.55] text-agent-text">
        <span className="whitespace-pre-wrap">{content}</span>
        <span className="ml-0.5 inline-block h-3.5 w-0.5 animate-pulse bg-agent-text-muted align-text-bottom" />
      </div>
    );
  }
  return (
    <div className="px-3">
      <MarkdownText text={content} className="text-[13px] leading-[1.55] text-agent-text" />
    </div>
  );
}

function ThinkingBlock({ text }: { readonly text: string }): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  return (
    <div>
      <button
        onClick={() => { setExpanded(!expanded); }}
        className="flex items-center gap-1.5 text-[12px] text-agent-text-dim hover:text-agent-text-muted"
        type="button"
      >
        <Icon name={expanded ? "chevron-down" : "chevron-right"} size={12} />
        <span>Thinking...</span>
      </button>
      {expanded && (
        <div className="mt-1.5 ml-5 whitespace-pre-wrap text-[12px] leading-[1.5] text-agent-text-muted">
          {text}
        </div>
      )}
    </div>
  );
}

/** Stable content-derived ID for data-testid attributes (lowercase alphanumeric, max 12 chars). */
function contentId(text: string): string | null {
  const id = text.toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 12);
  return id || null; // return null for empty content — skip data-testid
}

function StreamingIndicator(): JSX.Element {
  return (
    <div className="flex items-center gap-2 text-[12px] text-agent-text-dim">
      <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-[#FF8400]" />
      Thinking...
    </div>
  );
}

function ConnectionStatus({ error }: { readonly error: AgentFailure | null }): JSX.Element | null {
  if (!error) return null;
  return (
    <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-3 py-2 text-[12px] text-red-400 flex items-center gap-1.5">
      <Icon name="circle-alert" size={13} />
      {agentFailureCopy(error)}
    </div>
  );
}

export interface AgentMessageListProps {
  readonly messages: readonly { role: string; content: string; displayContent?: string; attachments?: readonly ChatMessageAttachment[] }[];
  readonly streamingContent: string;
  readonly isStreaming: boolean;
  readonly error: AgentFailure | null;
  readonly contentBlocks: readonly ContentBlock[];
  readonly toolCalls: readonly PendingToolCall[];
  readonly toolResults: readonly CompletedToolResult[];
  readonly onApproveToolCall: (id: string) => void;
  readonly onDenyToolCall: (id: string) => void;
  readonly renderToolCall: (tc: PendingToolCall) => ReactNode;
  readonly scrollRef?: React.Ref<HTMLDivElement>;
  readonly runtime?: AppRuntime;
  /** Background class for sticky user messages (must match parent pane). Defaults to bg-surface-tertiary. */
  readonly stickyBg?: string;
  /** Tool call IDs currently owned by the pending-prompt panel; skipped here. */
  readonly skipToolCallIds?: ReadonlySet<string>;
  /** Resolves a decision for a tool_call; when non-null, DecisionSummary replaces the full renderer. */
  readonly resolveDecision?: (toolCallId: string) => ResolvedDecision | null;
}

export function AgentMessageList({
  messages,
  streamingContent,
  isStreaming,
  error,
  contentBlocks,
  toolCalls,
  renderToolCall,
  scrollRef,
  runtime,
  stickyBg = "bg-surface-tertiary",
  skipToolCallIds,
  resolveDecision,
}: AgentMessageListProps): JSX.Element {
  const hasCurrentTurn = contentBlocks.length > 0 || isStreaming;
  const lastMsg = messages[messages.length - 1];
  const userMessageClass = `-mx-6 px-6 pt-1 pb-1 ${stickyBg} space-y-1.5`;
  // Order fix (PONG scramble): contentBlocks are the complete chronological
  // stream once present — hydration fills them with the FULL transcript in
  // ordinal order and every live push mirrors into them. The old cross-pass
  // content-equality dedup collapsed REPEATED texts across turns and
  // scrambled the transcript; Pass 1 renders only when no blocks exist.
  let displayMessages: typeof messages = messages;
  if (contentBlocks.length > 0) {
    displayMessages = [];
  } else if (hasCurrentTurn && lastMsg?.role === "assistant") {
    displayMessages = messages.slice(0, -1);
  }
  const displayMessageUserIds = new Set(
    displayMessages
      .filter((msg) => msg.role === "user")
      .map((msg) => contentId(visibleUserMessageText(msg.content)))
      .filter((id): id is string => id !== null),
  );
  const displayMessageAssistantIds = new Set(
    displayMessages
      .filter((msg) => msg.role === "assistant")
      .map((msg) => contentId(msg.content))
      .filter((id): id is string => id !== null),
  );

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden px-6 pt-4 pb-4 space-y-3.5">
      <ConnectionStatus error={error} />

      {displayMessages.map((msg, i) =>
        // Skip duplicate consecutive user messages (trigger executor + stream_chat both persist)
        (i > 0 && msg.role === "user" && displayMessages[i - 1]?.role === "user" && sameVisibleUserMessage(msg.content, displayMessages[i - 1]?.content ?? "")) ? null :
        // ASK_USER_MARKER_PREFIX messages are now rendered — the wrapper
        // is stripped by `visibleUserMessageText` so the bubble shows
        // just the user's selected options.
        msg.role === "user" && msg.content.startsWith("[User denied tool:") ? null :
        msg.role === "user" && msg.content.startsWith("[Autonomous trigger task]") ? (
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          <div key={`msg-${i}`} data-testid={contentId(msg.content) ? `chat-msg-user-${contentId(msg.content)}` : undefined} className={userMessageClass}>
            <UserMessageBubble content={extractTriggerPrompt(msg.content)} />
          </div>
        ) :
        msg.role === "user" ? (
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          <div key={`msg-${i}`} data-testid={contentId(msg.content) ? `chat-msg-user-${contentId(msg.content)}` : undefined} className={userMessageClass}>
            <UserMessageBubble content={msg.content} displayContent={msg.displayContent} attachments={msg.attachments} />
            {msg.attachments && msg.attachments.length > 0 && runtime && (
              <MessageAttachments attachments={msg.attachments} runtime={runtime} />
            )}
          </div>
        ) : (
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          <div key={`msg-${i}`} data-testid={contentId(msg.content) ? `chat-msg-asst-${contentId(msg.content)}` : undefined}>
            <AssistantBubble content={msg.content} isStreaming={false} />
          </div>
        ),
      )}

      {((): React.ReactNode[] => {
        // Render blocks strictly in agent-emitted order. We used to
        // swap `[tool_call, text]` → `[text, tool_call]` so the
        // approval card sat at the bottom, but pending approvals now
        // live in their own panel above the composer, so the swap is
        // unnecessary. For multi-tool runs the swap also produced
        // confusing `[TC1, T, TC2]` from `[TC1, TC2, T]`.
        const elements: React.ReactNode[] = [];
        for (let j = 0; j < contentBlocks.length; j++) {
          const block = contentBlocks[j];
          if (!block) continue;
          if (block.type === "thinking") {
            const cid = contentId(block.text);
            elements.push(<div key={`cb-${String(j)}`} data-testid={cid ? `chat-cb-think-${cid}` : undefined}><ThinkingBlock text={block.text} /></div>);
          } else if (block.type === "tool_call") {
            const tc = toolCalls.find((t) => t.id === block.toolCallId);
            if (!tc) continue;
            // Panel owns the pending-active prompt — skip in feed.
            if (skipToolCallIds?.has(tc.id)) continue;
            const decision = resolveDecision?.(tc.id) ?? null;
            elements.push(
              <div key={`cb-${String(j)}`} data-testid={`chat-cb-tc-${tc.id}`}>
                {decision ? (
                  <DecisionSummary decision={decision} runtime={runtime} />
                ) : (
                  renderToolCall(tc)
                )}
              </div>,
            );
          } else if (block.type === "text") {
            const cid = contentId(block.text);
            if (cid && displayMessageAssistantIds.has(cid)) continue;
            elements.push(<div key={`cb-${String(j)}`} data-testid={cid ? `chat-cb-text-${cid}` : undefined}><AssistantBubble content={block.text} isStreaming={false} /></div>);
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          } else if (block.type === "user_message") {
            if (j > 0 && contentBlocks[j - 1]?.type === "user_message" && sameVisibleUserMessage((contentBlocks[j - 1] as { text: string }).text, block.text)) continue;
            if (block.text.startsWith(ASK_USER_MARKER_PREFIX)) continue;
            if (block.text.startsWith("[User denied tool:")) continue;
            const cid = contentId(block.text);
            if (cid && displayMessageUserIds.has(cid)) continue;
            if (block.text.startsWith("[Autonomous trigger task]")) {
              elements.push(
                <div key={`cb-${String(j)}`} data-testid={cid ? `chat-cb-umsg-${cid}` : undefined} className={userMessageClass}>
                  <UserMessageBubble content={extractTriggerPrompt(block.text)} />
                </div>
              );
            } else {
              elements.push(
                <div key={`cb-${String(j)}`} data-testid={cid ? `chat-cb-umsg-${cid}` : undefined} className={userMessageClass}>
                  <UserMessageBubble content={block.text} />
                </div>
              );
            }
          }
        }
        return elements;
      })()}

      {isStreaming && streamingContent && (
        <div data-testid="chat-item-streaming">
          <AssistantBubble content={streamingContent} isStreaming />
        </div>
      )}
      {isStreaming && !streamingContent && (
        <div data-testid="chat-item-streaming">
          <StreamingIndicator />
        </div>
      )}
    </div>
  );
}
